import { SelectionList, SelectionListProps } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import {
  Dispatch,
  RefCallback,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react';

import { CategorizedListProps } from 'components/categorizedList';
import { categoryTitles } from 'components/categorizedList/utils/categories';
import { CategorizedListCategoryNamedId } from 'utils/useCategorizedList/useCategorizedList.types';
import { useIntersectionObserver } from 'utils/useIntersectionObserver';

import styles from './categorizedListCategories.module.scss';
import { CreateItem } from './components/createItem/createItem';
import { EmptyItem } from './components/emptyItem/emptyItem';
import { FavoriteItemLayout } from './components/favoriteItemLayout/favoriteItemLayout';
import { LoadingItem } from './components/loadingItem/loadingItem';

/** Represents the categorized list categories props. */
export type CategorizedListCategoriesProps<TItem> = Pick<
  CategorizedListProps<TItem>,
  | 'categories'
  | 'selectedItems'
  | 'isMultiple'
  | 'isItemDisabled'
  | 'isItemFavorite'
  | 'identityFn'
  | 'asyncStatus'
  | 'hasNextPage'
  | 'nextPage'
  | 'refetch'
  | 'ItemContent'
  | 'CreateItemContent'
> &
  Pick<SelectionListProps<TItem>, 'onChange'> & {
    /** Whether the list is active. */
    isActive: boolean;
    /** The search term. */
    searchTerm: string;
    /** The setter for changing the categories element. */
    setCategoriesElement: Dispatch<SetStateAction<HTMLUListElement | null>>;
    /** The setter for changing the categories section elements. */
    setCategoriesSectionElements: Dispatch<
      SetStateAction<
        Partial<Record<CategorizedListCategoryNamedId, HTMLLIElement>>
      >
    >;
    /** The setter for changing the visible category ids. */
    setVisibleCategoryIds: Dispatch<
      SetStateAction<readonly CategorizedListCategoryNamedId[]>
    >;
  };

/** Represents the list of categories. */
export const CategorizedListCategories = <TItem,>({
  categories,
  selectedItems,
  asyncStatus,
  hasNextPage,
  isMultiple,
  isActive,
  searchTerm,
  ItemContent,
  CreateItemContent,
  isItemFavorite,
  isItemDisabled = () => false,
  identityFn,
  nextPage,
  refetch,
  onChange,
  setCategoriesElement,
  setCategoriesSectionElements,
  setVisibleCategoryIds,
}: CategorizedListCategoriesProps<TItem>): JSX.Element => {
  const { observeElement } = useIntersectionObserver({
    onIntersection: (entry) => {
      const { dataset } = entry.target as HTMLElement;
      const categoryId = dataset.id as CategorizedListCategoryNamedId;

      setVisibleCategoryIds((visibleCategoryIds) => {
        if (entry.isIntersecting) {
          return visibleCategoryIds.every((id) => id !== categoryId)
            ? visibleCategoryIds.concat(categoryId)
            : visibleCategoryIds;
        }

        return visibleCategoryIds.some((id) => id === categoryId)
          ? visibleCategoryIds.filter((id) => id !== categoryId)
          : visibleCategoryIds;
      });
    },
  });

  /** Handles setting the categories section ref element. */
  const setCategoriesSectionElement = useCallback<RefCallback<HTMLElement>>(
    (categoryElement) => {
      if (categoryElement) {
        const categoryId = categoryElement.dataset
          .id as CategorizedListCategoryNamedId;

        observeElement(categoryElement);
        setCategoriesSectionElements((categoriesSectionElements) => ({
          ...categoriesSectionElements,
          [categoryId]: categoryElement,
        }));
      }
    },
    [observeElement, setCategoriesSectionElements],
  );

  const showEmpty = useMemo(
    () =>
      asyncStatus === 'fulfilled' &&
      categories.length === 1 &&
      categories[0].id === 'paginated' &&
      categories[0].items.length === 0,
    [asyncStatus, categories],
  );

  return (
    <SelectionList<TItem>
      className={styles.root}
      ref={setCategoriesElement}
      selectedValues={selectedItems as TItem[]}
      isMultiple={isMultiple}
      isActive={isActive}
      onChange={onChange}
      equalityFn={(a, b) => identityFn(a) === identityFn(b)}
      defaultActiveItemIndex={CreateItemContent ? 1 : 0}
    >
      {CreateItemContent && (
        <CreateItem searchTerm={searchTerm} refetch={refetch}>
          <CreateItemContent />
        </CreateItem>
      )}

      {categories.map((category) => (
        <SelectionList.Section
          key={category.id}
          data-id={category.id}
          ref={setCategoriesSectionElement}
          className={styles.section}
          title={categoryTitles[category.id]}
        >
          {category.items.map((item) => (
            <SelectionList.Item
              key={identityFn(item)}
              value={item}
              isDisabled={isItemDisabled(item)}
              contentClassName={classnames({
                [styles.disabled]: isItemDisabled(item),
              })}
            >
              <FavoriteItemLayout
                item={item}
                isFavorite={isItemFavorite(item)}
                isDisabled={isItemDisabled(item)}
              >
                <ItemContent />
              </FavoriteItemLayout>
            </SelectionList.Item>
          ))}

          {showEmpty && <EmptyItem searchTerm={searchTerm} />}
        </SelectionList.Section>
      ))}

      <LoadingItem
        asyncStatus={asyncStatus}
        hasNextPage={hasNextPage}
        nextPage={nextPage}
      />
    </SelectionList>
  );
};
